<template>
  <div>
    <MainForm
      :name="FORM_NAMES.storedValueCard"
      :show-step="isOnStep"
      :logic-callbacks="getLogicCallback"
      :ui-form="uiForm"
      :end-page-data="endPageData"
      :ui-modal-and-end-page="uiModalAndEndPage"
      ui-title="Main form title"
    >
      <StepModel
        :ui="uiBaseStep"
        :name="STEP_NAMES.welcome"
        :ui-title="`Welcome to ${BRAND.name}`"
        :ui-subtitle="`To get your ${BRAND.card} with ease, keep the below
          documents ready before you begin:`"
        ui-error-message="''"
        ui-button-text="Let's Start"
      >
        <FieldsetModel :ui="uiNoticeFieldset" :name="FIELDSET_NAMES.welcome">
          <ul>
            <li>Original Emirates ID</li>
          </ul>
        </FieldsetModel>
      </StepModel>

      <StepOCREmirates />

      <StepIdentityReview />

      <StepModel
        :ui="uiBaseStep"
        :name="STEP_NAMES.eligibility"
        ui-title="Contact"
        ui-subtitle="Thanks! Now, tell us a little bit more about you."
        ui-error-message="Please, review the files marked in red."
        ui-button-text="Send code"
      >
        <FieldsetEmail />
        <FieldsetMobilePhone />
      </StepModel>

      <StepOtp :change-phone-action="goToStep(STEP_NAMES.eligibility)" />

      <StepModel
        :ui="uiBaseStep"
        :name="STEP_NAMES.cardDetails"
        :ui-title="`Thanks, ${userName} 👌`"
        ui-subtitle="Your card is ready to send.
        We just need some more information so we can deliver it to you"
        ui-error-message="Please review the fields marked in red"
        ui-button-text="Continue"
        :ui-secondary-button="resetUploadsButtonText([FIELD_NAMES.pictureOnCard])"
        :ui-secondary-action="resetFields([FIELD_NAMES.pictureOnCard])"
      >
        <FieldsetModel
          :ui="uiBaseFieldset"
          :name="FIELDSET_NAMES.nameOnCard"
          ui-title="Your name on card"
        >
          <UiWrapper :name="FIELD_NAMES.nameOnCard" label="Full name">
            <InputText />
          </UiWrapper>
        </FieldsetModel>
        <FieldsetModel
          :ui="uiBaseFieldset"
          :name="FIELDSET_NAMES.pictureOnCard"
          ui-title="Do you want to personalise your card?"
          ui-subtitle="Add a picture to be on the cover of your card."
        >
          <UiWrapper :name="FIELD_NAMES.pictureOnCard" label="Browse a file" :max-file-size="4096">
            <UploadWrapper
              accept="image/jpeg, image/png"
              :changeable="true"
              :upload-service="async (file, callback) => {}"
              :uploaded-callback="() => {}"
            />
          </UiWrapper>
        </FieldsetModel>
        <FieldsetModel :ui="uiBaseFieldset" :name="FIELDSET_NAMES.consentCardAcceptance">
          <UiWrapper :name="FIELD_NAMES.consentCardAcceptance">
            <CheckWrapper>
              <Expandable
                excerpt="I, hereby consent to..."
                :extra-text="`(i) credit bureau(s) including but not limited to Al Etihad Credit
                Bureau Company and Equifax India (the “Credit Bureau”) disclosing my personal,
                financial and credit information (the “Information”), to Deem Finance
                LLC (“${BRAND.name}”) on an on-going basis.
                (ii) ${BRAND.name} accessing any Credit Bureau’s database to carry out credit
                checks and to obtain Information; and
                (iii) ${BRAND.name} disclosing and furnishing Information pertaining to my
                account(s) and related credit conduct to any Credit Bureau from time to time.
                (iv) ${BRAND.name} requesting and getting bank statements of my account(s)
                from any and all banks that I may have accounts with my bank for my statement
                of account through the Central Bank of the United Arab Emirates, and also for
                my bank to provide any and all information requested by ${BRAND.name} through the
                Central Bank of the United Arab Emirates, without taking additional consent.
                I further agree and understand that the Information is released without any
                liability or responsibility on the part of ${BRAND.name} and that the Information
                provided by ${BRAND.name} to any Credit Bureau will be made available by that
                Credit Bureau to its members.`"
              />
            </CheckWrapper>
          </UiWrapper>
        </FieldsetModel>
      </StepModel>
      <StepModel
        :ui="uiBaseStep"
        :name="STEP_NAMES.appointment"
        ui-title="Schedule your appointment"
        ui-subtitle="You are all set! Now we just need to meet
        you in person to complete the process."
        ui-error-message="Please review the fields marked in red"
      >
        <FieldsetAppointmentDate :name="FIELDSET_NAMES.appointmentSetDate" />
        <FieldsetModel
          :ui="uiBaseFieldset"
          :name="FIELDSET_NAMES.appointmentSetAddress"
          ui-columns="2"
          ui-title="We will meet in your office. Where is it?"
        >
          <UiWrapper
            :name="FIELD_NAMES.companyName"
            label="Company name"
            :validations="[
              {
                'errorMessage': 'This field must not be empty',
                'condition': notEmptyCondition,
                'name': 'companies-filled',
              },
            ]"
          >
            <Suggest
              data-forced-fullwidth="true"
              :exact-match="false"
              placeholder="Company name"
              :get-formatted-item="formatMatchedString"
              :get-suggestions="getCompanies"
            />
          </UiWrapper>

          <UiWrapper :name="FIELD_NAMES.address" label="Address line 1">
            <InputText data-forced-fullwidth="true" />
          </UiWrapper>
          <UiWrapper :name="FIELD_NAMES.additionalAddress" label="Address line 2" optional>
            <InputText />
          </UiWrapper>

          <UiWrapper :name="FIELD_NAMES.nearestLandmark" label="Nearest Landmark" optional>
            <InputText />
          </UiWrapper>

          <UiWrapper :name="FIELD_NAMES.areaStreetName" label="Area/Street name">
            <InputText data-forced-fullwidth="true" />
          </UiWrapper>
          <UiWrapper :name="FIELD_NAMES.poBox" label="PO Box">
            <InputText />
          </UiWrapper>
          <UiWrapper :name="FIELD_NAMES.emirate" label="Emirate">
            <Dropdown placeholder="Emirate" :options="EMIRATE_LIST" />
          </UiWrapper>
        </FieldsetModel>
      </StepModel>
      <StepModel
        :ui="uiBaseStep"
        :name="STEP_NAMES.setAccountPassword"
        :ui-title="`Thanks, ${userName} 👌`"
        ui-subtitle="Your digital card is ready!"
        ui-error-message="Please review the fields marked in red"
        ui-button="Confirm"
      >
        <FieldsetModel
          :ui="uiBaseFieldset"
          :name="FIELDSET_NAMES.setAccountPassword"
          ui-title="Set up a password"
          :ui-subtitle="`Set up a secure password to access
          your new ${BRAND.name} account. You will be asked
          to login in again with your new password.`"
        >
          <UiWrapper :name="FIELD_NAMES.accountPassword" label="A passphrase would be ideal">
            <InputPassword />
          </UiWrapper>
        </FieldsetModel>
      </StepModel>
    </MainForm>
  </div>
</template>

<script>
import MainForm from "@/lib/components/main-form";
import StepModel from "@/lib/components/models/step";
import FieldsetModel from "@/lib/components/models/fieldset";
import store from "@/store";
import { SET_PRODUCT_TYPE } from "@/lib/constants/mutations";
import UiWrapper from "@/forms/components/base/ui-wrapper";
import UploadWrapper from "@/forms/components/ui-dunia/upload-wrapper";
import CheckWrapper from "@/forms/components/ui-dunia/check-wrapper";

import {
  InputText,
  Suggest,
  Dropdown,
  InputPassword,
  Expandable
} from "@dunia/ui";

import FormComponentMixin from "@/lib/mixins/form-component-mixin";

import FIELD_NAMES from "@/forms/constants/field-names";

import { END_FORM } from "@/forms/constants/onboarding-logic";

import StepOtp from "@/forms/components/shared/steps/otp";
//import getGoogleTagManager from "@/forms/helpers/google-tag-manager";
import FieldsetEmail from "@/forms/components/shared/fieldsets/email";
import FieldsetMobilePhone from "@/forms/components/shared/fieldsets/mobile-phone";
import StepOCREmirates from "@/forms/components/shared/steps/ocr-emirates";
import StepIdentityReview from "@/forms/components/shared/steps/identity-review";
import FieldsetAppointmentDate from "@/forms/components/shared/fieldsets/appointment-date";

export default {
  name: "StoredValueCard",

  components: {
    MainForm,
    StepModel,
    FieldsetModel,
    UiWrapper,
    InputText,
    UploadWrapper,
    Suggest,
    CheckWrapper,
    Expandable,
    Dropdown,
    InputPassword,
    StepOtp,
    FieldsetEmail,
    FieldsetMobilePhone,
    StepOCREmirates,
    StepIdentityReview,
    FieldsetAppointmentDate
  },

  mixins: [FormComponentMixin],

  data: () => ({
    ID_FIELDS: [FIELD_NAMES.emiratesFrontCard, FIELD_NAMES.emiratesBackCard]
  }),
  mounted() {
   // getGoogleTagManager();
   var gtagID = process.env.VUE_APP_GA_MEASUREMENT_ID;
    if (gtagID) {
      (function(w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, "script", "dataLayer", `${gtagID}`);
    }
  },
  created: function() {
    store.commit(SET_PRODUCT_TYPE, { productType: "Card" });
  },
  methods: {
    finishCallback() {
      this.showEndPage({
        title:
          "Great!✌️ Now you can access to your account and start using you brand new Stored Value card!",
        actions: [{ click: END_FORM, label: "Go to my account" }]
      });
    }
  }
};
</script>
